import { FC, useCallback, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { ERROR_CODES } from '@shinetools/errors';

import { SensitiveOperationType } from '__generated__/GQL';
import { hasErrorCode } from 'common/hasErrorCode';
import useSMSProtectedSensitiveOperation from 'common/hooks/useSMSProtectedSensitiveOperation';
import Button from 'components/_core/Button';
import SunshineModal from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import PasscodeInput from 'components/PasscodeInput';
import { ClearAllDigitsFn } from 'components/PasscodeInput/types';
import logger from 'helpers/logger';

import { getErrorLocale } from './errors';
import locales from './locales';

const DEFAULT_TIMER = 180;

interface SMSVerificationModalProps {
  actionType: SensitiveOperationType;
  actionData: Record<string, unknown>;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (token: string) => void;
}

const SMSVerificationModal: FC<SMSVerificationModalProps> = ({
  actionData,
  actionType,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [hasAlreadyBeenOpened, setHasAlreadyBeenOpened] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [timer, setTimer] = useState(DEFAULT_TIMER);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const { initSensitiveOperation, phone, verifySensitiveOperationCode } =
    useSMSProtectedSensitiveOperation({
      actionData,
      actionType,
    });

  const onSubmit = useCallback(
    async (verificationCode: string, clearAllDigits: ClearAllDigitsFn) => {
      try {
        setIsVerifying(true);
        setError(undefined);
        const token = await verifySensitiveOperationCode(verificationCode);
        onSuccess(token);
        onClose();
      } catch (err) {
        clearAllDigits({ andFocusFirstInput: true });
        if (
          err instanceof ApolloError &&
          (hasErrorCode(err.graphQLErrors, ERROR_CODES.RATE_LIMIT) ||
            hasErrorCode(
              err.graphQLErrors,
              ERROR_CODES.MAXIMUM_NUMBER_OF_ATTEMPTS_REACHED_FOR_SENSITIVE_OPERATION,
            ))
        ) {
          setError(ERROR_CODES.RATE_LIMIT);
        } else if (
          err instanceof ApolloError &&
          hasErrorCode(
            err.graphQLErrors,
            ERROR_CODES.WRONG_SENSITIVE_OPERATION_VERIFICATION_CODE,
          )
        ) {
          setError(ERROR_CODES.WRONG_SENSITIVE_OPERATION_VERIFICATION_CODE);
        } else if (
          err instanceof ApolloError &&
          hasErrorCode(
            err.graphQLErrors,
            ERROR_CODES.SENSITIVE_OPERATION_VERIFICATION_CODE_EXPIRED,
          )
        ) {
          // We only want the user to be able to resend the code if the code expired
          setTimer(0);
        } else {
          setError('UNKNOWN');
          logger.error(err);
        }
      } finally {
        setIsVerifying(false);
      }
    },
    [onClose, onSuccess, verifySensitiveOperationCode],
  );

  useEffect(() => {
    if (isOpen && !hasAlreadyBeenOpened) {
      setHasAlreadyBeenOpened(true);
      initSensitiveOperation();
    }
  }, [initSensitiveOperation, isOpen, hasAlreadyBeenOpened]);

  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(
        () =>
          setTimer((previousTimer) => {
            if (previousTimer === 0) {
              return previousTimer;
            }

            return previousTimer - 1;
          }),
        1000,
      );

      return () => {
        clearInterval(interval);
      };
    }

    return () => {};
  }, [isOpen]);

  const timerText =
    timer > 1
      ? locales.formatString(locales.sentToPlural, {
          phone,
          timer,
        })
      : locales.formatString(locales.sentToSingular, {
          phone,
          timer,
        });

  const resendSMS = useCallback(async () => {
    try {
      setIsResending(true);
      await initSensitiveOperation();
      setTimer(DEFAULT_TIMER);
    } catch (err) {
      setError(locales.unknownError);
      logger.error(err);
    } finally {
      setIsResending(false);
    }
  }, [setIsResending, initSensitiveOperation, setTimer, setError]);

  const expirationManagement =
    timer > 0 ? (
      <Text marginTop="space-32">{timerText}</Text>
    ) : (
      <>
        <Text marginTop="space-32">{locales.codeExpired}</Text>
        <Button
          isDisabled={isResending}
          isLoading={isResending}
          marginTop="space-16"
          onClick={resendSMS}
          variant="inline-secondary"
        >
          {locales.resend}
        </Button>
      </>
    );

  return (
    <SunshineModal isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header />
      <SunshineModal.Body>
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>
        <Text marginBottom="space-32">{locales.description}</Text>
        <PasscodeInput
          disabled={isVerifying || error === ERROR_CODES.RATE_LIMIT}
          hidden
          length={6}
          onSubmit={onSubmit}
        />
        {error === undefined ? null : (
          <Text marginTop="space-16" variant="danger">
            {getErrorLocale(error)}
          </Text>
        )}
        {error !== ERROR_CODES.RATE_LIMIT ? expirationManagement : null}
      </SunshineModal.Body>
      <SunshineModal.Footer>
        <Button
          isLoading={isVerifying}
          onClick={() => {
            // There is an auto submit but we want the button to appear for consistency
            () => {};
          }}
        >
          {locales.confirm}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default SMSVerificationModal;
